/**
 * Language Table Decoder Ring — browser widget for the enterprise language registry.
 * Client-side only.
 *
 * SIP-842: register data is injected by the app via LanguageDecoder.setEmbeddedRegister
 * (register.json imported in index.js); no remote fetch to S3 or GitLab. An optional
 * data-register-url still works for standalone/remote embeds.
 *
 * Strapi / React: raw HTML embeds often strip <script> tags inside the embed.
 * Load this file from data-lang-js (see language-decoder-v2-strapi.html) or once
 * from your front-end layout. Retries + MutationObserver wire the UI when the
 * widget mounts late.
 */
(function () {
  'use strict';

  //SIP-842: register data injected by the app via setEmbeddedRegister (see index.js / register.json import).
  var EMBEDDED_REGISTER = null;

  function setEmbeddedRegister(data) {
    EMBEDDED_REGISTER = data;
  }
  var DEFAULT_REGISTER_URL = '';
  var LANG_DECODER_UI_BOUND = 'data-lang-decoder-bound';

  function normTag(s) {
    return String(s || '')
      .trim()
      .toLowerCase()
      .replace(/_/g, '-');
  }

  function collectStrings(lang) {
    var out = [];
    function push(value) {
      if (value == null) return;
      var text = String(value).trim();
      if (text) out.push(text);
    }
    push(lang.bcp47Tag);
    push(lang.displayName);
    if (Array.isArray(lang.use)) lang.use.forEach(push);
    if (Array.isArray(lang.mappings)) {
      for (var i = 0; i < lang.mappings.length; i++) {
        var mapping = lang.mappings[i];
        push(mapping.code);
        push(mapping.name);
        push(mapping.notes);
        push(mapping.system);
      }
    }
    return out;
  }

  function buildHaystack(strings) {
    return strings.join('\n').toLowerCase();
  }

  function tokenizeForExact(input) {
    var set = {};
    var parts = input.split(/\s+/);
    for (var i = 0; i < parts.length; i++) {
      var token = parts[i].replace(/^[^\w]+|[^\w]+$/g, '');
      if (token.length >= 2) set[token.toLowerCase()] = true;
    }
    return Object.keys(set);
  }

  function buildIndex(languages) {
    return languages.map(function (lang) {
      var strings = collectStrings(lang);
      var haystack = buildHaystack(strings);
      var tokens = {};
      strings.forEach(function (value) {
        tokenizeForExact(value).forEach(function (token) {
          tokens[token] = true;
        });
      });
      if (lang.bcp47Tag) tokens[normTag(lang.bcp47Tag)] = true;
      return { lang: lang, haystack: haystack, tokens: Object.keys(tokens) };
    });
  }

  function parseRegister(text) {
    var json = typeof text === 'string' ? JSON.parse(text) : text;
    var languages = json && json.register && json.register.languages;
    if (!Array.isArray(languages)) {
      throw new Error('Expected JSON with register.languages array.');
    }
    return {
      version: typeof json.version === 'string' ? json.version : '',
      languages: languages,
      index: buildIndex(languages),
    };
  }

  function scoreMatch(entry, qNorm, qTag) {
    var lang = entry.lang;
    var haystack = entry.haystack;
    var tokens = entry.tokens;
    if (!qNorm) return 0;
    if (haystack.indexOf(qNorm) === -1) return 0;

    var score = 50;
    var tag = normTag(lang.bcp47Tag);
    if (qTag && tag === qTag) score += 200;
    if (Array.isArray(lang.mappings)) {
      for (var i = 0; i < lang.mappings.length; i++) {
        var mapping = lang.mappings[i];
        var code =
          mapping && mapping.code != null ? String(mapping.code).trim().toLowerCase() : '';
        if (code && (code === qNorm || code === qTag)) score += 150;
      }
    }
    if (tokens.indexOf(qNorm) !== -1 || (qTag && tokens.indexOf(qTag) !== -1)) score += 40;
    var displayName = String(lang.displayName || '').toLowerCase();
    if (displayName === qNorm) score += 80;
    return score;
  }

  function search(state, query) {
    var raw = query.trim();
    if (!raw) return [];
    var qNorm = raw.toLowerCase();
    var qTag = normTag(raw);
    var scored = state.index
      .map(function (entry, i) {
        return { i: i, score: scoreMatch(entry, qNorm, qTag) };
      })
      .filter(function (item) {
        return item.score > 0;
      })
      .sort(function (a, b) {
        return b.score - a.score || a.i - b.i;
      });
    return scored.map(function (item) {
      return item.i;
    });
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function mappingDisplayRank(system) {
    var name = String(system != null ? system : '').trim();
    if (name === 'Disney Spec') return 0;
    if (name === 'Wonderland') return 1;
    return 2;
  }

  function sortMappingsForDisplay(mappings) {
    if (!Array.isArray(mappings)) return [];
    return mappings
      .map(function (mapping, index) {
        return { mapping: mapping, index: index };
      })
      .sort(function (a, b) {
        var rankA = mappingDisplayRank(a.mapping && a.mapping.system);
        var rankB = mappingDisplayRank(b.mapping && b.mapping.system);
        if (rankA !== rankB) return rankA - rankB;
        return a.index - b.index;
      })
      .map(function (item) {
        return item.mapping;
      });
  }

  function initEmbed(root) {
    if (!root || root.getAttribute(LANG_DECODER_UI_BOUND) === 'true') return false;

    var els = {
      q: root.querySelector('.ld-q'),
      clear: root.querySelector('.ld-clear'),
      status: root.querySelector('.ld-status'),
      main: root.querySelector('.ld-main'),
      list: root.querySelector('.ld-list'),
      detail: root.querySelector('.ld-detail'),
      foot: root.querySelector('.ld-foot'),
    };

    if (!els.q || !els.clear || !els.status || !els.main || !els.list || !els.detail || !els.foot) {
      return false;
    }

    var registerUrl = root.getAttribute('data-register-url') || DEFAULT_REGISTER_URL;
    var state = { languages: [], index: [], version: '' };
    var selectedIdx = -1;
    var debounceTimer;

    function setStatus(message, isError) {
      els.status.textContent = message;
      if (isError) els.status.classList.add('err');
      else els.status.classList.remove('err');
    }

    function renderList(indices, queryEmpty) {
      els.list.innerHTML = '';
      if (queryEmpty) {
        var placeholder = document.createElement('p');
        placeholder.className = 'empty';
        placeholder.textContent = state.languages.length
          ? 'Type a name, tag, or code to search — the full list is not shown here.'
          : 'No data loaded.';
        els.list.appendChild(placeholder);
        return;
      }
      if (!indices.length) {
        var empty = document.createElement('p');
        empty.className = 'empty';
        empty.textContent = state.languages.length
          ? 'No matches. Try another code or name.'
          : 'No data loaded.';
        els.list.appendChild(empty);
        return;
      }
      indices.forEach(function (languageIndex) {
        var language = state.languages[languageIndex];
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'row';
        button.setAttribute('role', 'option');
        button.dataset.idx = String(languageIndex);
        button.setAttribute('aria-selected', String(selectedIdx === languageIndex));

        var title = document.createElement('div');
        title.className = 'row-title';
        title.textContent = language.displayName || '(no display name)';

        var meta = document.createElement('div');
        meta.className = 'row-meta';
        meta.textContent = language.bcp47Tag || '—';

        button.appendChild(title);
        button.appendChild(meta);
        button.addEventListener('click', function () {
          selectLanguage(languageIndex);
        });
        els.list.appendChild(button);
      });
    }

    function renderDetail(language) {
      if (!language) {
        els.detail.innerHTML = '<p class="empty">Select a match to see the full entry.</p>';
        return;
      }
      var usePills =
        Array.isArray(language.use) && language.use.length
          ? '<div class="pills">' +
            language.use
              .map(function (item) {
                return '<span class="pill">' + escapeHtml(item) + '</span>';
              })
              .join('') +
            '</div>'
          : '';
      var rows = sortMappingsForDisplay(language.mappings)
        .map(function (mapping) {
          return (
            '<tr><td>' +
            escapeHtml(mapping.system != null ? mapping.system : '') +
            '</td><td>' +
            escapeHtml(mapping.code != null ? mapping.code : '') +
            '</td><td>' +
            escapeHtml(mapping.name != null ? mapping.name : '') +
            '</td><td class="notes">' +
            escapeHtml(mapping.notes != null ? mapping.notes : '') +
            '</td></tr>'
          );
        })
        .join('');

      els.detail.innerHTML =
        '<h2>' +
        escapeHtml(language.displayName || '') +
        '</h2>' +
        '<p class="tagline">' +
        escapeHtml(language.bcp47Tag || '') +
        '</p>' +
        usePills +
        '<table><thead><tr><th>System</th><th>Code</th><th>Name</th><th>Notes</th></tr></thead><tbody>' +
        (rows || '<tr><td colspan="4" class="empty">No mappings</td></tr>') +
        '</tbody></table>';
    }

    function selectLanguage(languageIndex) {
      selectedIdx = languageIndex;
      var buttons = els.list.querySelectorAll('button.row');
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].setAttribute(
          'aria-selected',
          buttons[i].dataset.idx === String(languageIndex) ? 'true' : 'false'
        );
      }
      renderDetail(state.languages[languageIndex]);
    }

    function runSearch() {
      var query = els.q.value;
      var queryEmpty = !query.trim();
      var indices = queryEmpty ? [] : search(state, query);
      renderList(indices, queryEmpty);
      if (queryEmpty) {
        selectedIdx = -1;
        renderDetail(null);
        return;
      }
      if (indices.length) {
        var first = indices.indexOf(selectedIdx) !== -1 ? selectedIdx : indices[0];
        selectLanguage(first);
      } else {
        selectedIdx = -1;
        renderDetail(null);
      }
    }

    function applyLoaded() {
      els.q.disabled = false;
      els.clear.disabled = false;
      els.main.hidden = false;
      els.foot.textContent = state.version ? 'Register version: ' + state.version : '';
      setStatus('Loaded ' + state.languages.length + ' languages. Search to begin.', false);
      runSearch();
    }

    function setControlsEnabled(enabled) {
      if (!enabled) {
        els.q.disabled = true;
        els.clear.disabled = true;
      }
    }

    function fetchRegisterFromUrl(url) {
      setStatus('Loading registry…');
      setControlsEnabled(false);
      var promise = url
        ? fetch(url, { cache: 'no-store' }).then(function (res) {
            if (!res.ok) {
              throw new Error('Failed to load registry: HTTP ' + res.status);
            }
            return res.text();
          })
        : EMBEDDED_REGISTER
          ? Promise.resolve(EMBEDDED_REGISTER)
          : Promise.reject(new Error('No register source configured (set data-register-url or inject register.json).'));
      return promise
        .then(function (data) {
          var parsed = parseRegister(data);
          state.languages = parsed.languages;
          state.index = parsed.index;
          state.version = parsed.version;
          applyLoaded();
        })
        .catch(function (error) {
          setStatus(String((error && error.message) || error), true);
        })
        .finally(function () {
          setControlsEnabled(true);
        });
    }

    els.q.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(runSearch, 140);
    });

    els.clear.addEventListener('click', function () {
      els.q.value = '';
      runSearch();
      els.q.focus();
    });

    root.setAttribute(LANG_DECODER_UI_BOUND, 'true');
    fetchRegisterFromUrl(registerUrl);
    return true;
  }

  function autoInitEmbeddedUi() {
    var roots = document.querySelectorAll('.lang-decoder-embed');
    for (var i = 0; i < roots.length; i++) {
      initEmbed(roots[i]);
    }
  }

  function tryAutoInitEmbeddedUi() {
    var tries = 0;
    var maxTries = 80;
    var delayMs = 120;
    function tick() {
      autoInitEmbeddedUi();
      var pending = document.querySelector(
        '.lang-decoder-embed:not([' + LANG_DECODER_UI_BOUND + '="true"])'
      );
      if (!pending) return;
      tries += 1;
      if (tries < maxTries) setTimeout(tick, delayMs);
    }
    tick();
  }

  function observeLangDecoderMount() {
    if (typeof MutationObserver === 'undefined' || !document.documentElement) return;
    var scheduled = false;
    function run() {
      autoInitEmbeddedUi();
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        run();
      });
    }
    var mo = new MutationObserver(schedule);
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (typeof document !== 'undefined') {
    function boot() {
      tryAutoInitEmbeddedUi();
      observeLangDecoderMount();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  window.LanguageDecoder = {
    parseRegister: parseRegister,
    autoInitEmbeddedUi: autoInitEmbeddedUi,
    initEmbed: initEmbed,
    setEmbeddedRegister: setEmbeddedRegister,
  };
})();
